/** Graphical analytics for one strategy: a day/month/year toggle, a stats
 * header, an equity curve and a per-period P&L bar chart. Works on any
 * strategy's real closed trades. */

import { useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  reportsApi,
  type Granularity,
  type ReportExecutionMode,
  type TimeseriesResponse,
} from "@/lib/reportsApi";
import { summarize } from "./analyticsMath";
import { EquityCurve, PnlBars } from "./AnalyticsCharts";
import { DrilldownPanel } from "./DrilldownPanel";

const GRANULARITIES: readonly { key: Granularity; label: string }[] = [
  { key: "day", label: "Day" },
  { key: "month", label: "Month" },
  { key: "year", label: "Year" },
];

export function AnalyticsSection({
  strategyId,
  mode = "LIVE",
}: Readonly<{ strategyId: string; mode?: ReportExecutionMode }>): ReactElement {
  const [granularity, setGranularity] = useState<Granularity>("month");
  const [selected, setSelected] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["report-timeseries", strategyId, granularity, mode],
    queryFn: () => reportsApi.timeseries(granularity, strategyId, mode),
  });
  return (
    <section className="card mb-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <i className="ti ti-chart-histogram text-lg text-accent" />
          <h3>Performance analytics</h3>
        </div>
        <GranularityTabs
          value={granularity}
          onChange={(g) => {
            setGranularity(g);
            setSelected(null);
          }}
        />
      </div>
      <Body data={q.data} loading={q.isPending} error={q.isError} onSelect={setSelected} />
      {selected !== null && (
        <DrilldownPanel
          strategyId={strategyId}
          period={selected}
          granularity={granularity}
          mode={mode}
          onClose={() => {
            setSelected(null);
          }}
        />
      )}
    </section>
  );
}

function Body({
  data,
  loading,
  error,
  onSelect,
}: Readonly<{
  data: TimeseriesResponse | undefined;
  loading: boolean;
  error: boolean;
  onSelect: (period: string) => void;
}>): ReactElement {
  if (loading) return <p className="text-sm text-ink-secondary">Loading analytics…</p>;
  if (error || data === undefined)
    return <p className="text-sm text-danger">Couldn&apos;t load analytics.</p>;
  if (data.buckets.length === 0) return <EmptyState />;
  return (
    <>
      <StatsHeader data={data} />
      <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <EquityCurve buckets={data.buckets} />
        <PnlBars buckets={data.buckets} onSelect={onSelect} />
      </div>
    </>
  );
}

function EmptyState(): ReactElement {
  return (
    <p className="text-sm text-ink-secondary">
      No closed trades in this range yet — charts appear once this strategy books its first trade.
    </p>
  );
}

function StatsHeader({ data }: Readonly<{ data: TimeseriesResponse }>): ReactElement {
  const s = summarize(data.buckets);
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Stat label="Total P&L" value={rupees(s.totalPnl)} tone={tone(s.totalPnl)} />
      <Stat label="Trades" value={String(s.trades)} />
      <Stat label="Win rate" value={`${(s.winRate * 100).toFixed(0)}%`} />
      <Stat label="Periods" value={String(data.buckets.length)} />
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "text-ink",
}: Readonly<{ label: string; value: string; tone?: string }>): ReactElement {
  return (
    <div className="rounded-ctl bg-surface-2 p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-ink-secondary">{label}</div>
      <div className={`mt-0.5 font-medium ${tone}`}>{value}</div>
    </div>
  );
}

function GranularityTabs({
  value,
  onChange,
}: Readonly<{ value: Granularity; onChange: (g: Granularity) => void }>): ReactElement {
  return (
    <div className="flex gap-1.5" role="group" aria-label="Granularity">
      {GRANULARITIES.map((g) => (
        <button
          key={g.key}
          type="button"
          aria-pressed={value === g.key}
          className={`rounded-ctl border px-3 py-1 text-xs font-medium ${
            value === g.key
              ? "border-accent-border bg-accent-bg text-accent"
              : "border-line bg-surface-1 text-ink-secondary hover:bg-surface-2"
          }`}
          onClick={() => {
            onChange(g.key);
          }}
        >
          {g.label}
        </button>
      ))}
    </div>
  );
}

function tone(v: number): string {
  if (v > 0) return "text-success";
  if (v < 0) return "text-danger";
  return "text-ink";
}

function rupees(v: number): string {
  const sign = v < 0 ? "-" : "";
  return `${sign}Rs ${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
}
